import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { ITokenModel, ITokenService, SERVICE_TOKEN } from 'src/app/core/api/token/interface';
import { DeviceStoreKey, NativeService } from 'src/app/core/native/native.service';
import { DialogService } from 'src/app/core/api/dialog/dialog.service';
import { CryptoService, UserInfoModel } from 'src/app/core/api/crypto-js/crypto.service';
import { HospitalLanguageService } from 'src/app/core/i18n/hospital-language.service';
import { normalizeHospitalType } from 'src/app/core/i18n/hospital-type.util';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {

  platform: string = '';
  store_key: string = `_rememberMe`;
  form = new FormGroup({
    rememberMe: new FormControl(false)
  });
  model: any = {};
  fields: FormlyFieldConfig[] = [
    // 医療機関コード
    {
      type: 'flex-layout',
      templateOptions: {
        fxLayout: 'column',
        fxLayoutAlign: 'start stretch',
      },
      fieldGroup: [
        {
          type: 'input',
          key: 'hospitalId',
          templateOptions: {
            required: true,
            placeholder: '医療機関コードを入力',
            maxLength: 254,
            appearance :'outline',
            attributes: {
              style: "text-align: left;"
            }
          },
          validators: {
            validation: ['hankaku'],
          }
        }
      ]
    },
    // ユーザー名
    {
      type: 'flex-layout',
      templateOptions: {
        fxLayout: 'column',
        fxLayoutAlign: 'start stretch',
      },
      fieldGroup: [
        {
          type: 'input',
          key: 'username',
          templateOptions: {
            required: true,
            placeholder: 'ユーザー名を入力',
            maxLength: 254,
            appearance :'outline',
            attributes: {
              style: "text-align: left;"
            }
          },
          validators: {
            validation: ['hankaku'],
          }
        }
      ]
    },
    // パスワード
    {
      type: 'flex-layout',
      templateOptions: {
        fxLayout: 'column',
        fxLayoutAlign: 'start stretch',
      },
      fieldGroup: [
        {
          type: 'input',
          key: 'password',
          templateOptions: {
            type: 'password',
            required: true,
            placeholder: 'パスワードを入力',
            minLength: 6,
            maxLength: 64,
            appearance :'outline',
            attributes: {
              style: "text-align: left;"
            }
          },
          validators: {
            validation: ['hankaku'],
          }
        }
      ]
    },

  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private nativeService: NativeService,
    private dialogService: DialogService,
    private cryptoService: CryptoService,
    private hospitalLanguage: HospitalLanguageService,
    @Inject(SERVICE_TOKEN) private tokenService: ITokenService,
  ) { }

  ngOnInit(): void {
    const hospitalIdField = this.fields[0]?.fieldGroup?.[0];
    if (hospitalIdField?.templateOptions) {
      hospitalIdField.templateOptions.placeholder = `${this.hospitalLanguage.t('医療機関コード')}を入力`;
    }

    this.route.paramMap.subscribe(params => {
      // プラットフォーム情報
      this.platform = params.get('platform') || '';
      if (this.platform.length > 0 && !localStorage.hasOwnProperty(DeviceStoreKey.DeviceType)) {
        this.nativeService.setData(DeviceStoreKey.DeviceType, this.platform);
      }
      if (localStorage.hasOwnProperty(this.store_key)) {
        // LocalStorageからユーザー情報を取得する
        let bol: string = localStorage.getItem(this.store_key) || 'false';
        if (bol === 'true') {
          let info = this.cryptoService.get(`_s`);
          if (info) {
            this.model = info;
            this.form.patchValue({ rememberMe: true });
          }
        }
      }
    });
  }

  /**
   * ログイン
   */
  onSubmit() {
    if (this.form.valid) {
      // LocalStorage中の簡易ログインフラグを更新する
      localStorage.setItem(this.store_key, this.form.value.rememberMe ? 'true' : 'false');
      let json = JSON.stringify(this.model)
      this.http.post<any>('/login', json)
      .subscribe(
        (res: any) => {
          if (res.resultCode === 200) {
            this.save(res.data);
            this.gotoIndex({
              accessToken: res.data.token,
              expiresIn: 30 * 60 * 1000
            });
          } else {
            let options = {
              title: '',
              message: `<div class="mat-dialog-oneline"><div class="mat-dialog-oneline-child">${res.message}</div></div>`,
              cancelText: '閉じる',
            };
            this.dialogService.open(options, true);
          }
          
        }, 
        (ev: HttpErrorResponse) => {
          let options = {
            title: '',
            message: `<div class="mat-dialog-oneline"><div class="mat-dialog-oneline-child">ログインに失敗しました。</div></div>`,
            cancelText: '閉じる',
          };
          this.dialogService.open(options, true);
        }
      );  
      
    }
  }

  /**
   * ログインと簡易ログインの成功処理
   */
  gotoIndex(data: ITokenModel) {
    // LocalStorageにトークン情報を保存する
    this.tokenService.set(data);
    // AndroidのSharedPreferencesにアクセストークンを同期する
    if (data.accessToken) {
      this.nativeService.syncToken(data.accessToken);
    }
    this.router.navigate(['/patient/new-list']);
  }

  /**
   * ユーザー名とパスワードを一時的にLocalStorageに保存する
   */
  save(data: any) {
    // username: ログインID（入力値）として保持。displayName: API返却 userName を保持。
    const displayName = data?.userName != null ? String(data.userName) : '';

    let info: UserInfoModel = {
      hospitalId: this.model.hospitalId,
      username: this.model.username,
      displayName,
      password: (this.form.value.rememberMe === true) ? this.model.password : '',
      departmentId: data.departmentId,
      hospitalType: normalizeHospitalType(data?.hospitalType ?? data?.hospital_type),
    };
    this.cryptoService.set(`_s`, info);
  }

}
